import sanitizeHtml from 'sanitize-html'
import { kPleadingCaptionBlockName } from '../custom_markdown_blocks/pleading-caption'
import {
  kArtifactLegalCitationCanOpenAttr,
  kArtifactLegalCitationIdAttr,
  kArtifactLegalCitationResolvedAttr,
  kArtifactReferenceMarkerCanOpenAttr,
  kArtifactReferenceMarkerIdAttr,
  kArtifactReferenceMarkerLabelAttr,
  kArtifactReferenceMarkerResolvedAttr,
} from './artifact-citation-helpers'

const kAllowedLinkProtocols = new Set(['http:', 'https:', 'mailto:'])

/**
 * Sanitize rendered Markdown before ProseMirror parses it.
 *
 * This policy is intentionally limited to HTML represented by the artifact
 * editor schema. Citation spans keep identity and display metadata only; viewer
 * URLs are always derived from live reference state.
 */
export function sanitizeAgentV2ArtifactEditorHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: [
      'p',
      'blockquote',
      'hr',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'pre',
      'code',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'a',
      'ul',
      'ol',
      'li',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'span',
      kPleadingCaptionBlockName,
    ],
    allowedAttributes: {
      a: ['href', 'title'],
      ol: ['start'],
      span: [
        kArtifactLegalCitationIdAttr,
        kArtifactLegalCitationResolvedAttr,
        kArtifactLegalCitationCanOpenAttr,
        kArtifactReferenceMarkerIdAttr,
        kArtifactReferenceMarkerLabelAttr,
        kArtifactReferenceMarkerResolvedAttr,
        kArtifactReferenceMarkerCanOpenAttr,
      ],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesAppliedToAttributes: ['href'],
    allowProtocolRelative: false,
    disallowedTagsMode: 'discard',
    nonTextTags: ['style', 'script', 'textarea', 'option', 'xmp', 'noscript', 'iframe', 'svg', 'math'],
    parseStyleAttributes: false,
    transformTags: {
      a: (tagName: string, attributes: Record<string, string>): sanitizeHtml.Tag => {
        const href = sanitizeLinkHref(attributes.href)
        if (!href) return { tagName: 'span', attribs: {} }

        return {
          tagName,
          attribs: {
            href,
            ...(attributes.title ? { title: attributes.title } : {}),
          },
        }
      },
    },
  })
}

function sanitizeLinkHref(href: string | undefined): string | null {
  const trimmedHref = href?.trim()
  if (!trimmedHref || !/^[a-z][a-z\d+.-]*:/i.test(trimmedHref)) return null

  try {
    return kAllowedLinkProtocols.has(new URL(trimmedHref).protocol.toLowerCase()) ? trimmedHref : null
  } catch {
    return null
  }
}
